/**
 * Approvals and policy state for the console: state.ext.approvals and state.ext.policy from
 * the hub (kept live by SSE 'ext'), or demo data in mock mode. Answers and rule changes go
 * through the hub API; in mock mode they change the demo data in place.
 */
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type {
  ApprovalBatchDecision,
  ApprovalBatchItem,
  ApprovalBatchResult,
  ApprovalDecision,
  ApprovalRequest,
  ApprovalsState,
  PolicyRule,
  PolicyRuleInput,
  PolicySimulationRequest,
  PolicySimulationResult,
  PolicyState,
} from '@orgus/protocol';
import { APPROVALS_BATCH_PATH, checkAskAnswer, isAskKind } from '@orgus/protocol';
import { defaultReason } from '@orgus/policy';
import { api, apiPost } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { mockApprovals, mockBurst, mockHistory, mockPolicy, simulateLocally } from './mock.ts';
import { sortRequests } from './decision.ts';

export interface ApprovalsView {
  /** loading: no state yet. missing: the hub has no approvals module. */
  phase: 'loading' | 'error' | 'missing' | 'ready';
  approvals: ApprovalsState | null;
  policy: PolicyState | null;
  mock: boolean;
}

function seedMock(): void {
  const st = useOrgus.getState();
  const now = Date.now();
  const sessions = Object.values(st.sessions);
  if (!st.ext.approvals) st.applyExt({ ext: 'approvals', type: 'state', data: mockApprovals(sessions, now) });
  if (!st.ext.policy) st.applyExt({ ext: 'policy', type: 'state', data: mockPolicy(Object.keys(st.projects), now) });
}

/** In mock mode, fill state.ext.approvals and state.ext.policy with demo data once. */
function useMockSeed(): void {
  const { ready, mock, has } = useOrgus(useShallow((s) => ({ ready: s.phase === 'ready', mock: s.mode === 'mock', has: !!s.ext.approvals && !!s.ext.policy })));
  useEffect(() => {
    if (mock && ready && !has) seedMock();
  }, [mock, ready, has]);
}

let burstSeeded = false;

/** On demo data, add the bulk demo burst (mockBurst) once, when a page that answers in bulk opens. */
export function useMockBurst(): void {
  const { mock, has } = useOrgus(useShallow((s) => ({ mock: s.mode === 'mock', has: !!s.ext.approvals })));
  useEffect(() => {
    if (!mock || !has || burstSeeded) return;
    burstSeeded = true;
    const st = useOrgus.getState();
    const cur = st.ext.approvals as ApprovalsState;
    st.applyExt({ ext: 'approvals', type: 'state', data: { ...cur, pending: [...cur.pending, ...mockBurst(Object.values(st.sessions), Date.now())] } satisfies ApprovalsState });
  }, [mock, has]);
}

export function useApprovals(): ApprovalsView {
  useMockSeed();
  const { phase, mode, approvals, policy } = useOrgus(
    useShallow((s) => ({
      phase: s.phase,
      mode: s.mode,
      approvals: (s.ext.approvals as ApprovalsState | undefined) ?? null,
      policy: (s.ext.policy as PolicyState | undefined) ?? null,
    })),
  );
  const mock = mode === 'mock';
  if (phase === 'loading') return { phase: 'loading', approvals, policy, mock };
  if (phase === 'error') return { phase: 'error', approvals, policy, mock };
  if (!approvals && !mock) return { phase: 'missing', approvals, policy, mock };
  if (!approvals) return { phase: 'loading', approvals, policy, mock };
  return { phase: 'ready', approvals, policy, mock };
}

/** Pending approvals only (cheap selector for counts and Needs you). */
export function usePendingApprovals(): ApprovalRequest[] {
  useMockSeed();
  return useOrgus((s) => (s.ext.approvals as ApprovalsState | undefined)?.pending ?? EMPTY);
}
const EMPTY: ApprovalRequest[] = [];

/** Pending requests in the one order every surface uses (decision.ts): soonest deadline, then highest risk, then longest wait. */
export function sortPending(list: ApprovalRequest[]): ApprovalRequest[] {
  return sortRequests(list);
}

function setExt(name: string, data: unknown): void {
  useOrgus.getState().applyExt({ ext: name, type: 'state', data });
}

/** Answer a request. Resolves with the decided request. */
export async function decideApproval(id: string, d: ApprovalDecision): Promise<ApprovalRequest> {
  const st = useOrgus.getState();
  if (st.mode === 'mock' && st.ext.approvals) {
    await new Promise((r) => setTimeout(r, 350));
    return mockDecide(id, d);
  }
  const res = await apiPost<{ approval: ApprovalRequest }>(`/api/approvals/${encodeURIComponent(id)}`, d);
  // Show it at once; the hub's broadcast confirms it a moment later.
  showDecided([res.approval]);
  return res.approval;
}

/** Take decided requests out of pending at once; the hub's broadcast confirms it a moment later. */
function showDecided(done: readonly ApprovalRequest[]): void {
  const now = useOrgus.getState().ext.approvals as ApprovalsState | undefined;
  if (!now || !done.length) return;
  const ids = new Set(done.map((a) => a.id));
  setExt('approvals', { ...now, pending: now.pending.filter((x) => !ids.has(x.id)), recent: [...done, ...now.recent.filter((x) => !ids.has(x.id))].slice(0, 50) });
}

/**
 * Answer many requests with one answer (POST /api/approvals/batch, ADR 0072). Each id is checked
 * and audited by the hub as a single answer; the result says per id what went through.
 */
export async function decideBatch(body: ApprovalBatchDecision): Promise<ApprovalBatchResult> {
  const st = useOrgus.getState();
  const cur = st.ext.approvals as ApprovalsState | undefined;
  if (st.mode === 'mock' && cur) {
    await new Promise((r) => setTimeout(r, 450));
    const results: ApprovalBatchItem[] = body.ids.map((id) => {
      const p = (useOrgus.getState().ext.approvals as ApprovalsState).pending.find((x) => x.id === id);
      if (p && body.answer === 'allow' && p.risk === 'high' && !body.confirmHighRisk) {
        return { id, ok: false, status: 409, error: { what: 'This high-risk request was not allowed.', why: 'Allowing a high-risk request in bulk needs your confirmation.', next: 'Confirm the high-risk requests, or allow this one on its own.', code: 'confirm-high-risk' } };
      }
      if (p && body.answer === 'allow' && isAskKind(p.kind)) {
        return { id, ok: false, status: 400, error: { what: 'This question was not answered.', why: 'A question, plan or form needs its own answer.', next: 'Answer it on its own.', code: 'ask-in-batch' } };
      }
      try {
        const pattern = body.patterns?.[id] ?? p?.suggestedPattern;
        return { id, ok: true, approval: mockDecide(id, { answer: body.answer, scope: body.scope ?? 'once', ...(body.note ? { note: body.note } : {}), ...(body.scope === 'session' ? { pattern: pattern ?? '' } : {}) }) };
      } catch (e) {
        return { id, ok: false, status: 409, error: { what: e instanceof Error ? e.message : 'It was not answered.' } };
      }
    });
    const decided = results.filter((r) => r.ok).length;
    return { batchId: 'apb_DEMO', results, decided, failed: results.length - decided };
  }
  const res = await apiPost<ApprovalBatchResult>(APPROVALS_BATCH_PATH, body);
  showDecided(res.results.flatMap((r) => (r.ok && r.approval ? [r.approval] : [])));
  return res;
}

/** Demo mode: the hub's single answer against the store. Throws as the hub would refuse. */
function mockDecide(id: string, d: ApprovalDecision): ApprovalRequest {
  const st = useOrgus.getState();
  const cur = st.ext.approvals as ApprovalsState;
  {
    const p = cur.pending.find((x) => x.id === id);
    if (!p) throw new Error('That request was already answered.');
    const now = Date.now();
    const scope = d.scope ?? 'once';
    // The hub's checks of an answer to a question, plan or form (400 ask-scope, ask-invalid).
    if (isAskKind(p.kind) && scope !== 'once') throw new Error('The answer was not sent. A question, plan or form is answered once. Answer it once.');
    const checked = isAskKind(p.kind) && p.ask ? checkAskAnswer(p.ask, d.answer, d.ask) : null;
    if (checked && !checked.ok) throw new Error(`The answer was not sent. ${checked.why} ${checked.next}`);
    let ruleId: string | undefined;
    if (d.answer === 'allow' && scope === 'project') {
      const pol = st.ext.policy as PolicyState | undefined;
      const rule: PolicyRule = {
        id: `pol_DEMO${now.toString(36).toUpperCase()}`,
        scope: p.projectId ? { kind: 'project', projectId: p.projectId } : { kind: 'org' },
        match: { tool: p.tool, ...(d.pattern ? { pattern: d.pattern } : {}) },
        decision: 'allow',
        reason: d.note || 'Made a rule by you from an approval.',
        createdBy: 'you',
        createdAt: now,
        hits: 0,
      };
      ruleId = rule.id;
      if (pol) setExt('policy', { ...pol, version: pol.version + 1, rules: [...pol.rules, rule], updatedAt: now });
    }
    const done: ApprovalRequest = {
      ...p,
      state: d.answer === 'allow' ? 'allowed' : 'denied',
      decidedBy: 'you',
      decidedAt: now,
      decidedFrom: 'this browser (demo)',
      scope,
      ...(d.note ? { note: d.note } : {}),
      ...(ruleId ? { ruleId } : {}),
      ...(d.attachmentIds?.length ? { attachmentIds: d.attachmentIds } : {}),
      ...(checked?.ok && Object.keys(checked.answer).length ? { askAnswer: checked.answer } : {}),
    };
    setExt('approvals', {
      ...cur,
      pending: cur.pending.filter((x) => x.id !== id),
      recent: [done, ...cur.recent].slice(0, 50),
      sessionAllows:
        d.answer === 'allow' && scope === 'session'
          ? [...(cur.sessionAllows ?? []), { sessionId: p.sessionId, machineId: p.machineId, tool: p.tool ?? '', pattern: d.pattern ?? '', by: 'you', at: now }]
          : cur.sessionAllows,
    } satisfies ApprovalsState);
    return done;
  }
}

// ── Policy rules ───────────────────────────────────────────────────────────────

export async function saveRule(input: PolicyRuleInput, id?: string): Promise<PolicyRule> {
  const st = useOrgus.getState();
  const pol = st.ext.policy as PolicyState | undefined;
  if (st.mode === 'mock' && pol) {
    await new Promise((r) => setTimeout(r, 300));
    const now = Date.now();
    const prev = id ? pol.rules.find((r) => r.id === id) : undefined;
    const rule: PolicyRule = {
      ...(prev ?? { id: `pol_DEMO${now.toString(36).toUpperCase()}`, createdBy: 'you', createdAt: now, hits: 0 }),
      scope: input.scope,
      match: input.match,
      decision: input.decision,
      reason: input.reason ?? defaultReason(input),
      enabled: input.enabled ?? prev?.enabled ?? true,
      ...(prev ? { updatedBy: 'you', updatedAt: now } : {}),
    };
    setExt('policy', { ...pol, version: pol.version + 1, updatedAt: now, rules: prev ? pol.rules.map((r) => (r.id === id ? rule : r)) : [...pol.rules, rule] });
    return rule;
  }
  const res = await api<{ rule: PolicyRule }>(id ? `/api/policy/rules/${encodeURIComponent(id)}` : '/api/policy/rules', { method: id ? 'PUT' : 'POST', body: input });
  return res.rule;
}

export async function deleteRule(id: string): Promise<void> {
  const st = useOrgus.getState();
  const pol = st.ext.policy as PolicyState | undefined;
  if (st.mode === 'mock' && pol) {
    await new Promise((r) => setTimeout(r, 300));
    setExt('policy', { ...pol, version: pol.version + 1, updatedAt: Date.now(), rules: pol.rules.filter((r) => r.id !== id) });
    return;
  }
  await api(`/api/policy/rules/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function simulate(req: PolicySimulationRequest): Promise<PolicySimulationResult> {
  const st = useOrgus.getState();
  const pol = st.ext.policy as PolicyState | undefined;
  if (st.mode === 'mock' && pol) {
    await new Promise((r) => setTimeout(r, 450));
    const now = Date.now();
    let candidate = req.rules ?? pol.rules;
    if (req.without) candidate = candidate.filter((r) => !req.without!.includes(r.id));
    if (req.rule) {
      const r: PolicyRule = { id: req.rule.id ?? 'candidate', createdBy: 'you', createdAt: now, reason: req.rule.reason ?? '', scope: req.rule.scope, match: req.rule.match, decision: req.rule.decision, enabled: req.rule.enabled };
      candidate = candidate.some((x) => x.id === r.id) ? candidate.map((x) => (x.id === r.id ? r : x)) : [...candidate, r];
    }
    return simulateLocally(mockHistory(Object.values(st.sessions), now), pol.rules, candidate, now);
  }
  return apiPost<PolicySimulationResult>('/api/policy/simulate', req);
}
